// LC-481: "Company Being Represented" (DBA capture) on loan creation.
//
// When the Loan Originator's company or branch has DBAs in Encompass Settings,
// the broker picks the TPO legal name or one of the DBAs. The backend
// (/api/v1/organization/<org_id>/company_representation_options/) returns the
// options; this file turns the selected option into the create-loan payload
// and the field writer values.
//
//   Field    | Legal name selected | DBA selected
//   315      | legal name          | "[legal name] dba [DBA name]"
//   TPO.X24  | blank               | DBA name, when no branch is selected
//   TPO.X48  | blank               | DBA name, when a branch is selected

import { getOriginatorCompanyName, stripChannelSuffix } from "./originator-company-name";

export const COMPANY_REPRESENTATION_LEGAL = "legal";
export const COMPANY_REPRESENTATION_DBA = "dba";

const clean = (value) => (typeof value === "string" ? value.trim() : "");

export { stripChannelSuffix };

export const formatDbaCompanyName = (legalName, dbaName) =>
	`${stripChannelSuffix(legalName)} dba ${clean(dbaName)}`;

/** Options from the backend in the shape the select components use. */
export const toCompanyRepresentationSelectOptions = (options) =>
	(Array.isArray(options) ? options : []).map((option) => ({
		...option,
		label: option?.display_name || "",
		value: option?.value,
		typeLabel: option?.label,
	}));

/**
 * The selection sent with the create-loan payload as
 * loan_data.tpo.companyRepresentation. Returns null when nothing is selected.
 */
export const buildCompanyRepresentation = (option, isBranch) => {
	if (!option) {
		return null;
	}

	const type =
		option?.type === COMPANY_REPRESENTATION_DBA
			? COMPANY_REPRESENTATION_DBA
			: COMPANY_REPRESENTATION_LEGAL;

	return {
		type,
		legalName: stripChannelSuffix(option?.legal_name),
		dbaName: type === COMPANY_REPRESENTATION_DBA ? clean(option?.dba_name) : "",
		isBranch: isBranch === true,
		displayName: option?.display_name || "",
	};
};

const readCompanyRepresentation = (loanData) => {
	const selection = loanData?.tpo?.companyRepresentation;
	const legalName = stripChannelSuffix(selection?.legalName);

	if (!selection || !legalName) {
		return null;
	}

	if (selection.type === COMPANY_REPRESENTATION_DBA && clean(selection.dbaName)) {
		return { ...selection, legalName, dbaName: clean(selection.dbaName) };
	}

	if (selection.type === COMPANY_REPRESENTATION_LEGAL) {
		return { ...selection, legalName, dbaName: "" };
	}

	return null;
};

/**
 * Field writer values for URLA Part 4 company name (315) and the DBA fields.
 * Without a selection (no DBAs on the org) only 315 is written, with the
 * LC-669 legal name, and TPO.X24 / TPO.X48 are left as they are.
 */
export const getOriginatorCompanyFieldValues = (loanData, organization) => {
	const selection = readCompanyRepresentation(loanData);

	if (!selection) {
		return [{ id: 315, value: getOriginatorCompanyName(loanData, organization) }];
	}

	if (selection.type === COMPANY_REPRESENTATION_DBA) {
		const isBranch = selection.isBranch === true;
		return [
			{ id: 315, value: formatDbaCompanyName(selection.legalName, selection.dbaName) },
			{ id: "TPO.X24", value: isBranch ? "" : selection.dbaName },
			{ id: "TPO.X48", value: isBranch ? selection.dbaName : "" },
		];
	}

	return [
		{ id: 315, value: selection.legalName },
		{ id: "TPO.X24", value: "" },
		{ id: "TPO.X48", value: "" },
	];
};

/**
 * Edit flow: keep a DBA chosen at creation. Returns the saved 315 when the
 * saved TPO.X24 / TPO.X48 show that a DBA was selected, otherwise null.
 */
export const getSavedDbaCompanyName = ({ savedCompanyName, savedCompanyDba, savedBranchDba }) => {
	const companyName = clean(savedCompanyName);
	const dbaName = clean(savedBranchDba) || clean(savedCompanyDba);

	if (!companyName || !dbaName) {
		return null;
	}

	return companyName.toLowerCase().endsWith(` dba ${dbaName}`.toLowerCase())
		? companyName
		: null;
};

// Contains "provide a value" so the Loan Originator card highlights the field.
export const COMPANY_REPRESENTATION_REQUIRED_ERROR =
	"Please provide a value for Company Being Represented.";
